import React, { useMemo } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Check, X, Plus, Megaphone, CaretDown, Image, ToggleRight, ToggleLeft,
  Pencil, VideoCamera, UploadSimple, Trash
} from '@phosphor-icons/react';
import { AdStatCard } from './_shared';

const AD_PLACEMENTS = [
  { group: 'Home Page', placements: [
    { id: 'homepage_top', name: 'Top Banner', desc: 'Full width banner at the very top of home page', size: '1200 x 200 px' },
    { id: 'homepage_middle', name: 'Middle Section', desc: 'Banner between categories and listings', size: '1200 x 150 px' },
    { id: 'homepage_bottom', name: 'Bottom Banner', desc: 'Banner at the bottom of home page', size: '1200 x 150 px' },
  ]},
  { group: 'Listing Pages', placements: [
    { id: 'listing_sidebar', name: 'Sidebar Ad', desc: 'Vertical banner on listing detail sidebar', size: '300 x 600 px' },
    { id: 'listing_below', name: 'Below Details', desc: 'Horizontal banner below listing details', size: '800 x 150 px' },
    { id: 'listing_specific', name: 'Specific Listing Page', desc: 'Show on a particular listing page only', size: '800 x 150 px' },
  ]},
  { group: 'Search & Browse', placements: [
    { id: 'search_top', name: 'Search Results Top', desc: 'Banner above search results', size: '1200 x 100 px' },
  ]},
];

const LISTING_CATEGORIES = [
  { id: 'parking', name: 'Parking' }, { id: 'hotels', name: 'Hotels' }, { id: 'gyms', name: 'Gyms' },
  { id: 'yoga_wellness', name: 'Yoga / Wellness' }, { id: 'hospital', name: 'Hospital' }, { id: 'home', name: 'Home' },
  { id: 'coworking', name: 'Coworking' }, { id: 'meeting_rooms', name: 'Meeting Rooms' }, { id: 'event_spaces', name: 'Events' },
  { id: 'studios', name: 'Studios' }, { id: 'storage', name: 'Storage' }, { id: 'shops', name: 'Shops' },
  { id: 'malls', name: 'Malls' }, { id: 'halls', name: 'Halls' }, { id: 'housing_societies', name: 'Societies' },
  { id: 'religious_places', name: 'Religious' }, { id: 'salons', name: 'Salons' }, { id: 'clubs', name: 'Clubs' },
  { id: 'restaurants', name: 'Restaurants' }, { id: 'sports', name: 'Sports' }, { id: 'ev_charging', name: 'EV Charging' },
  { id: 'education', name: 'Education' },
  { id: 'astrology', name: 'Astrology' },
];

const AdsView = ({ ads, showAddAd, adForm, adStats, addingToPlacement, uploadingAd, listings, setShowAddAd, setAdForm, setAddingToPlacement, handleCreateAd, handleToggleAd, handleDeleteAd, handleUpdateAd, handleUploadAdMedia }) => {
  const getAdsForPlacement = (placementId) => ads.filter(a => a.position === placementId);
  // Memoize the live-listings dropdown options so we don't re-filter on every keystroke in the ad form
  const liveListings = useMemo(() => (listings || []).filter(l => l.status === 'live'), [listings]);
  const fileInputRef = React.useRef(null);
  const [expandedPlacements, setExpandedPlacements] = React.useState({});
  const [editingAd, setEditingAd] = React.useState(null);
  const [editForm, setEditForm] = React.useState({});

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) handleUploadAdMedia(file);
    e.target.value = '';
  };

  const getPlacementSize = (placementId) => {
    const p = AD_PLACEMENTS.flatMap(g => g.placements).find(p => p.id === placementId);
    if (!p) return null;
    const match = p.size.match(/(\d+)\s*x\s*(\d+)/i);
    return match ? { w: match[1], h: match[2] } : null;
  };

  const togglePlacement = (id) => {
    setExpandedPlacements(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const startEdit = (ad) => {
    setEditingAd(ad.ad_id);
    setEditForm({
      title: ad.title || '', type: ad.type || 'banner', media_url: ad.media_url || '',
      link_url: ad.link_url || '', width: ad.width || '', height: ad.height || '',
      listing_id: ad.listing_id || '', category_id: ad.category_id || '',
      schedule_start: ad.schedule_start || '', schedule_end: ad.schedule_end || '',
    });
  };

  const saveEdit = async () => {
    await handleUpdateAd(editingAd, editForm);
    setEditingAd(null);
  };

  const isListingPlacement = (positionId) => positionId?.startsWith('listing_');

  return (
    <div>
      {/* Stats Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
        <AdStatCard value={adStats.total} label="Total Ads" iconBg="bg-indigo-500/15" iconColor="text-indigo-400" icon={Megaphone} />
        <AdStatCard value={adStats.active} label="Active" iconBg="bg-emerald-500/15" iconColor="text-emerald-400" icon={Check} />
        <AdStatCard value={adStats.banners} label="Banners" iconBg="bg-amber-500/15" iconColor="text-amber-400" icon={Image} />
        <AdStatCard value={adStats.videos} label="Videos" iconBg="bg-purple-500/15" iconColor="text-purple-400" icon={VideoCamera} />
      </div>

      {/* Placement Groups */}
      <div className="space-y-8" data-testid="ads-placements">
        {AD_PLACEMENTS.map(group => (
          <div key={group.group}>
            <h3 className="text-white font-semibold text-base mb-3 flex items-center gap-2">
              <div className="w-1 h-5 bg-indigo-500 rounded-full" />
              {group.group}
            </h3>
            <div className="space-y-2">
              {group.placements.map(placement => {
                const placementAds = getAdsForPlacement(placement.id);
                const isExpanded = expandedPlacements[placement.id] !== false;
                return (
                  <div key={placement.id} className="bg-[#161b26] border border-[#252d3d] rounded-xl overflow-hidden" data-testid={`placement-${placement.id}`}>
                    {/* Collapsible Header */}
                    <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-[#1a2033] transition-colors"
                      onClick={() => togglePlacement(placement.id)} data-testid={`toggle-placement-${placement.id}`}>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#1e2536] rounded-lg flex items-center justify-center shrink-0">
                          <Image size={18} className="text-[#6b7a99]" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="text-white font-medium text-sm">{placement.name}</h4>
                            {placementAds.length > 0 && (
                              <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-indigo-500/20 text-indigo-400">
                                {placementAds.length}
                              </span>
                            )}
                          </div>
                          <p className="text-[#6b7a99] text-xs">{placement.desc}</p>
                          <span className="text-[#4a5578] text-[10px]">Recommended: {placement.size}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            const sz = getPlacementSize(placement.id);
                            setAddingToPlacement(placement.id);
                            setAdForm({ title: '', type: 'banner', media_url: '', link_url: '', position: placement.id, width: sz?.w || '', height: sz?.h || '', listing_id: '', category_id: '', schedule_start: '', schedule_end: '' });
                            if (!isExpanded) setExpandedPlacements(prev => ({ ...prev, [placement.id]: true }));
                          }}
                          className="bg-indigo-600 hover:bg-indigo-700 rounded-lg text-xs h-8 gap-1"
                          data-testid={`add-ad-${placement.id}`}
                        >
                          <Plus size={14} /> Add Ad
                        </Button>
                        <CaretDown size={16} className={`text-[#6b7a99] transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                      </div>
                    </div>

                    {/* Expandable Content */}
                    {isExpanded && (
                      <div className="border-t border-[#252d3d] px-4 pb-4 pt-2">
                        {/* Inline Add Ad Form */}
                        {addingToPlacement === placement.id && (
                          <div className="bg-[#1a2033] border border-indigo-500/30 rounded-xl p-4 mb-3" data-testid="add-ad-form">
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="text-white font-semibold text-xs">New Ad for {placement.name}</h4>
                              <button onClick={() => { setAddingToPlacement(null); setShowAddAd(false); }} className="text-[#6b7a99] hover:text-white"><X size={14} /></button>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                              <div>
                                <label className="text-[#6b7a99] text-[10px] mb-0.5 block">Title</label>
                                <Input value={adForm.title} onChange={e => setAdForm({ ...adForm, title: e.target.value })} placeholder="Ad title" className="bg-[#0f1219] border-[#252d3d] text-white h-8 rounded text-xs" data-testid="ad-title" />
                              </div>
                              <div>
                                <label className="text-[#6b7a99] text-[10px] mb-0.5 block">Type</label>
                                <Select value={adForm.type} onValueChange={v => setAdForm({ ...adForm, type: v })}>
                                  <SelectTrigger className="bg-[#0f1219] border-[#252d3d] text-white h-8 rounded text-xs" data-testid="ad-type"><SelectValue /></SelectTrigger>
                                  <SelectContent><SelectItem value="banner">Banner Image</SelectItem><SelectItem value="video">Video</SelectItem></SelectContent>
                                </Select>
                              </div>
                              <div className="md:col-span-2">
                                <label className="text-[#6b7a99] text-[10px] mb-0.5 block">Upload File or Enter URL</label>
                                <div className="flex gap-2 items-center">
                                  <input type="file" ref={fileInputRef} className="hidden" accept={adForm.type === 'video' ? 'video/*' : 'image/*'} onChange={handleFileSelect} />
                                  <button onClick={() => fileInputRef.current?.click()} disabled={uploadingAd}
                                    className="flex items-center gap-1 px-2.5 py-1.5 bg-[#252d3d] hover:bg-[#303a50] border border-[#3a4561] rounded text-[10px] text-white transition-colors disabled:opacity-50"
                                    data-testid="upload-ad-file">
                                    <UploadSimple size={12} /> {uploadingAd ? 'Uploading...' : 'Upload'}
                                  </button>
                                  <span className="text-[#4a5578] text-[10px]">or</span>
                                  <Input value={adForm.media_url} onChange={e => setAdForm({ ...adForm, media_url: e.target.value })} placeholder="Image/Video URL" className="bg-[#0f1219] border-[#252d3d] text-white h-8 rounded text-xs flex-1" data-testid="ad-media-url" />
                                </div>
                                {adForm.media_url && (
                                  <div className="mt-1.5 p-1.5 bg-[#0f1219] rounded border border-[#252d3d]">
                                    {adForm.type === 'video' ? (
                                      <div className="flex items-center gap-1.5 text-purple-400 text-[10px]"><VideoCamera size={12} /><span className="truncate">{adForm.media_url}</span></div>
                                    ) : (
                                      <img src={adForm.media_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${adForm.media_url}` : adForm.media_url} alt="Preview" className="max-h-16 rounded object-contain" onError={e => e.target.style.display='none'} />
                                    )}
                                  </div>
                                )}
                              </div>
                              <div>
                                <label className="text-[#6b7a99] text-[10px] mb-0.5 block">Link URL (optional)</label>
                                <Input value={adForm.link_url} onChange={e => setAdForm({ ...adForm, link_url: e.target.value })} placeholder="https://..." className="bg-[#0f1219] border-[#252d3d] text-white h-8 rounded text-xs" data-testid="ad-link-url" />
                              </div>
                              <div>
                                <label className="text-[#6b7a99] text-[10px] mb-0.5 block">Size (px)</label>
                                <div className="flex items-center gap-1.5">
                                  <Input type="number" value={adForm.width} onChange={e => setAdForm({ ...adForm, width: e.target.value })} placeholder="W" className="bg-[#0f1219] border-[#252d3d] text-white h-8 rounded text-xs w-20" data-testid="ad-width" />
                                  <span className="text-[#6b7a99] text-[10px]">x</span>
                                  <Input type="number" value={adForm.height} onChange={e => setAdForm({ ...adForm, height: e.target.value })} placeholder="H" className="bg-[#0f1219] border-[#252d3d] text-white h-8 rounded text-xs w-20" data-testid="ad-height" />
                                </div>
                              </div>
                              {isListingPlacement(placement.id) && (
                                <div>
                                  <label className="text-[#6b7a99] text-[10px] mb-0.5 block">Category (optional)</label>
                                  <Select value={adForm.category_id || 'all'} onValueChange={v => setAdForm({ ...adForm, category_id: v === 'all' ? '' : v })}>
                                    <SelectTrigger className="bg-[#0f1219] border-[#252d3d] text-white h-8 rounded text-xs" data-testid="ad-category-id"><SelectValue /></SelectTrigger>
                                    <SelectContent className="max-h-48">
                                      <SelectItem value="all">All Categories</SelectItem>
                                      {LISTING_CATEGORIES.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                                    </SelectContent>
                                  </Select>
                                </div>
                              )}
                              {placement.id === 'listing_specific' && (
                                <div>
                                  <label className="text-[#6b7a99] text-[10px] mb-0.5 block">Target Listing</label>
                                  <Select value={adForm.listing_id || 'all'} onValueChange={v => setAdForm({ ...adForm, listing_id: v === 'all' ? '' : v })}>
                                    <SelectTrigger className="bg-[#0f1219] border-[#252d3d] text-white h-8 rounded text-xs" data-testid="ad-listing-id"><SelectValue placeholder="Select listing..." /></SelectTrigger>
                                    <SelectContent className="max-h-48">
                                      <SelectItem value="all">All Listings</SelectItem>
                                      {liveListings.map(l => (
                                        <SelectItem key={l.listing_id} value={l.listing_id}>{l.title} ({l.category})</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                              )}
                              <div>
                                <label className="text-[#6b7a99] text-[10px] mb-0.5 block">Schedule Start</label>
                                <Input type="datetime-local" value={adForm.schedule_start} onChange={e => setAdForm({ ...adForm, schedule_start: e.target.value ? new Date(e.target.value).toISOString() : '' })} className="bg-[#0f1219] border-[#252d3d] text-white h-8 rounded text-xs" data-testid="ad-schedule-start" />
                              </div>
                              <div>
                                <label className="text-[#6b7a99] text-[10px] mb-0.5 block">Schedule End</label>
                                <Input type="datetime-local" value={adForm.schedule_end} onChange={e => setAdForm({ ...adForm, schedule_end: e.target.value ? new Date(e.target.value).toISOString() : '' })} className="bg-[#0f1219] border-[#252d3d] text-white h-8 rounded text-xs" data-testid="ad-schedule-end" />
                              </div>
                            </div>
                            <div className="flex gap-2 mt-3">
                              <Button size="sm" onClick={handleCreateAd} className="bg-indigo-600 hover:bg-indigo-700 rounded text-xs h-8 px-4" data-testid="save-ad">Create Ad</Button>
                              <Button size="sm" variant="ghost" onClick={() => { setAddingToPlacement(null); setShowAddAd(false); }} className="text-[#6b7a99] rounded text-xs h-8">Cancel</Button>
                            </div>
                          </div>
                        )}

                        {placementAds.length > 0 ? (
                          <div className="space-y-2">
                            {placementAds.map(ad => (
                              <div key={ad.ad_id}>
                                {editingAd === ad.ad_id ? (
                                  /* ======= INLINE EDIT FORM ======= */
                                  <div className="bg-[#1a2033] border border-indigo-500/20 rounded-lg p-3 space-y-2" data-testid={`edit-ad-form-${ad.ad_id}`}>
                                    <p className="text-indigo-400 text-[10px] font-semibold uppercase mb-1">Editing: {ad.title}</p>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                      <Input value={editForm.title} onChange={e => setEditForm({ ...editForm, title: e.target.value })} placeholder="Title"
                                        className="bg-[#0f1219] border-[#252d3d] text-white h-8 rounded text-xs" data-testid={`edit-title-${ad.ad_id}`} />
                                      <Select value={editForm.type} onValueChange={v => setEditForm({ ...editForm, type: v })}>
                                        <SelectTrigger className="bg-[#0f1219] border-[#252d3d] text-white h-8 rounded text-xs"><SelectValue /></SelectTrigger>
                                        <SelectContent><SelectItem value="banner">Banner</SelectItem><SelectItem value="video">Video</SelectItem></SelectContent>
                                      </Select>
                                      <Input value={editForm.media_url} onChange={e => setEditForm({ ...editForm, media_url: e.target.value })} placeholder="Media URL"
                                        className="bg-[#0f1219] border-[#252d3d] text-white h-8 rounded text-xs md:col-span-2" data-testid={`edit-media-${ad.ad_id}`} />
                                      <Input value={editForm.link_url} onChange={e => setEditForm({ ...editForm, link_url: e.target.value })} placeholder="Link URL"
                                        className="bg-[#0f1219] border-[#252d3d] text-white h-8 rounded text-xs" />
                                      <div className="flex items-center gap-1.5">
                                        <Input type="number" value={editForm.width} onChange={e => setEditForm({ ...editForm, width: e.target.value })} placeholder="W"
                                          className="bg-[#0f1219] border-[#252d3d] text-white h-8 rounded text-xs w-16" />
                                        <span className="text-[#6b7a99] text-[10px]">x</span>
                                        <Input type="number" value={editForm.height} onChange={e => setEditForm({ ...editForm, height: e.target.value })} placeholder="H"
                                          className="bg-[#0f1219] border-[#252d3d] text-white h-8 rounded text-xs w-16" />
                                      </div>
                                      {isListingPlacement(ad.position) && (
                                        <Select value={editForm.category_id || 'all'} onValueChange={v => setEditForm({ ...editForm, category_id: v === 'all' ? '' : v })}>
                                          <SelectTrigger className="bg-[#0f1219] border-[#252d3d] text-white h-8 rounded text-xs" data-testid={`edit-category-${ad.ad_id}`}><SelectValue placeholder="Category..." /></SelectTrigger>
                                          <SelectContent className="max-h-48">
                                            <SelectItem value="all">All Categories</SelectItem>
                                            {LISTING_CATEGORIES.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                                          </SelectContent>
                                        </Select>
                                      )}
                                      <Input type="datetime-local" value={editForm.schedule_start ? editForm.schedule_start.slice(0, 16) : ''} onChange={e => setEditForm({ ...editForm, schedule_start: e.target.value ? new Date(e.target.value).toISOString() : '' })}
                                        className="bg-[#0f1219] border-[#252d3d] text-white h-8 rounded text-xs" />
                                      <Input type="datetime-local" value={editForm.schedule_end ? editForm.schedule_end.slice(0, 16) : ''} onChange={e => setEditForm({ ...editForm, schedule_end: e.target.value ? new Date(e.target.value).toISOString() : '' })}
                                        className="bg-[#0f1219] border-[#252d3d] text-white h-8 rounded text-xs" />
                                    </div>
                                    <div className="flex gap-2 mt-1">
                                      <Button size="sm" onClick={saveEdit} className="bg-emerald-600 hover:bg-emerald-700 rounded text-xs h-7 px-3" data-testid={`save-edit-${ad.ad_id}`}>Save</Button>
                                      <Button size="sm" variant="ghost" onClick={() => setEditingAd(null)} className="text-[#6b7a99] rounded text-xs h-7 px-3">Cancel</Button>
                                    </div>
                                  </div>
                                ) : (
                                  /* ======= AD ROW ======= */
                                  <div className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg bg-[#0f1219] ${!ad.active ? 'opacity-60' : ''}`} data-testid={`ad-${ad.ad_id}`}>
                                    <div className="w-10 h-10 bg-[#1e2536] rounded-md flex items-center justify-center shrink-0 overflow-hidden">
                                      {ad.type === 'video' ? <VideoCamera size={16} className="text-purple-400" /> :
                                       ad.media_url ? <img src={ad.media_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${ad.media_url}` : ad.media_url} alt="" className="w-full h-full object-cover" /> :
                                       <Megaphone size={16} className="text-amber-400" />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-white text-xs font-medium truncate">{ad.title}</p>
                                      <p className="text-[#4a5578] text-[10px] truncate">
                                        {ad.type === 'video' ? 'Video' : 'Banner'}
                                        {ad.width && ad.height ? ` | ${ad.width}x${ad.height}px` : ''}
                                        {ad.category_id ? ` | ${LISTING_CATEGORIES.find(c => c.id === ad.category_id)?.name || ad.category_id}` : ''}
                                        {ad.listing_id ? ` | Listing: ${(listings || []).find(l => l.listing_id === ad.listing_id)?.title?.slice(0, 20) || ad.listing_id.slice(0, 12)}` : ''}
                                      </p>
                                      {(ad.schedule_start || ad.schedule_end) && (
                                        <p className="text-[#4a5578] text-[9px]">
                                          {ad.schedule_start ? `From: ${new Date(ad.schedule_start).toLocaleString()}` : ''}
                                          {ad.schedule_start && ad.schedule_end ? ' | ' : ''}
                                          {ad.schedule_end ? `Until: ${new Date(ad.schedule_end).toLocaleString()}` : ''}
                                        </p>
                                      )}
                                    </div>
                                    {/* Status Badge */}
                                    <span className={`px-2 py-0.5 rounded text-[9px] font-semibold whitespace-nowrap ${ad.active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}`}>
                                      {ad.active ? 'Live' : 'Paused'}
                                    </span>
                                    {/* Pause/Resume Toggle */}
                                    <button onClick={() => handleToggleAd(ad.ad_id)} className="p-1 group relative" title={ad.active ? 'Pause' : 'Resume'}
                                      data-testid={`toggle-ad-${ad.ad_id}`}>
                                      {ad.active ? <ToggleRight size={20} className="text-emerald-400" weight="fill" /> : <ToggleLeft size={20} className="text-amber-400" weight="fill" />}
                                    </button>
                                    {/* Edit */}
                                    <button onClick={() => startEdit(ad)} className="p-1 text-[#6b7a99] hover:text-indigo-400 transition-colors" title="Edit"
                                      data-testid={`edit-ad-${ad.ad_id}`}>
                                      <Pencil size={14} />
                                    </button>
                                    {/* Delete */}
                                    <button onClick={() => handleDeleteAd(ad.ad_id)} className="p-1 text-[#6b7a99] hover:text-red-400 transition-colors" title="Delete"
                                      data-testid={`delete-ad-${ad.ad_id}`}>
                                      <Trash size={14} />
                                    </button>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-[#4a5578] text-xs pl-[52px]">No ads in this placement</p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdsView;
